import { Select as SelectPrimitive } from '@base-ui/react/select'
import type * as React from 'react'
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon
} from '@/components/ui/icons'
import { cn } from '@/helpers/utils'

const Select = SelectPrimitive.Root

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
	return (
		<SelectPrimitive.Group
			className={cn('scroll-my-1 p-1', className)}
			data-slot='select-group'
			{...props}
		/>
	)
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
	return (
		<SelectPrimitive.Value
			className={cn('flex flex-1 text-left', className)}
			data-slot='select-value'
			{...props}
		/>
	)
}

function SelectTrigger({
	className,
	size = 'default',
	children,
	bare = false,
	...props
}: SelectPrimitive.Trigger.Props & {
	size?: 'sm' | 'default'
	/**
	 * Drops the field chrome — border, white fill, padding, fixed height and the
	 * trailing chevron — leaving `children` as the whole control.
	 *
	 * For triggers whose value already carries its own shape, such as a status
	 * pill that is itself the thing you click. Wrapping one of those in the
	 * default trigger nests a shape inside a box and pads it out to a field
	 * width, so it stops matching the read-only rendering of the same value.
	 * A bare trigger owns its own affordance (and its own chevron, if it wants
	 * one) through `children`.
	 */
	bare?: boolean
}) {
	if (bare) {
		return (
			<SelectPrimitive.Trigger
				className={cn(
					'cursor-pointer transition-all duration-150',
					'disabled:cursor-not-allowed disabled:opacity-50',
					className
				)}
				data-slot='select-trigger'
				nativeButton={false}
				render={
					<div className='data-disabled:cursor-not-allowed'>
						{children}
					</div>
				}
				{...props}
			/>
		)
	}

	return (
		<SelectPrimitive.Trigger
			className={cn(
				'flex w-fit cursor-pointer items-center justify-between gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3 font-normal transition-all duration-150',
				'hover:not-disabled:bg-neutral-50',
				'hover:not-disabled:shadow-focus-primary-100-xxs hover:not-disabled:outline hover:not-disabled:outline-1 hover:not-disabled:outline-neutral-300',
				'focus:shadow-focus-primary-100-xxs',
				'data-[popup-open]:shadow-focus-primary-100-xxs data-[popup-open]:outline data-[popup-open]:outline-1 data-[popup-open]:outline-neutral-300',
				'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:opacity-50',
				'data-placeholder:text-neutral-500',
				size === 'sm' ? 'h-10 text-sm' : 'h-11 text-base',
				className
			)}
			data-size={size}
			data-slot='select-trigger'
			nativeButton={false}
			render={
				<div className='data-disabled:cursor-not-allowed'>
					{children}
					<SelectPrimitive.Icon className='ml-auto shrink-0 text-neutral-500'>
						<ChevronDownIcon
							className='transition-transform duration-150 [[data-popup-open]_&]:rotate-180'
							size={16}
						/>
					</SelectPrimitive.Icon>
				</div>
			}
			{...props}
		/>
	)
}

function SelectContent({
	className,
	children,
	side = 'bottom',
	sideOffset = 4,
	align = 'start',
	alignOffset = 0,
	alignItemWithTrigger = false,
	container,
	...props
}: SelectPrimitive.Popup.Props &
	Pick<
		SelectPrimitive.Positioner.Props,
		'align' | 'alignOffset' | 'alignItemWithTrigger' | 'side' | 'sideOffset'
	> &
	Pick<SelectPrimitive.Portal.Props, 'container'>) {
	return (
		// `container` keeps the popup inside a modal that manages its own layer.
		// A drawer or dialog disables pointer events on the body and re-enables
		// them only within its own subtree, so a popup portalled to the body
		// lands outside it: taps and scrolls never reach the list, and the ones
		// that do read as interactions with the background and dismiss the modal.
		// Portalling into the modal itself sidesteps all of that. Defaults to the
		// body, which is right everywhere else.
		<SelectPrimitive.Portal container={container}>
			<SelectPrimitive.Positioner
				align={align}
				alignItemWithTrigger={alignItemWithTrigger}
				alignOffset={alignOffset}
				className='z-(--z-dropdown)'
				side={side}
				sideOffset={sideOffset}
			>
				<SelectPrimitive.Popup
					className={cn(
						'z-(--z-dropdown) max-h-[328px] overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-white p-1 px-1.5 shadow-lg',
						'origin-[var(--transform-origin)] transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
						className
					)}
					data-slot='select-content'
					{...props}
				>
					<SelectScrollUpButton />
					<SelectPrimitive.List>{children}</SelectPrimitive.List>
					<SelectScrollDownButton />
				</SelectPrimitive.Popup>
			</SelectPrimitive.Positioner>
		</SelectPrimitive.Portal>
	)
}

function SelectLabel({
	className,
	...props
}: SelectPrimitive.GroupLabel.Props) {
	return (
		<SelectPrimitive.GroupLabel
			className={cn(
				'px-2 py-1.5 font-medium text-neutral-500 text-xs',
				className
			)}
			data-slot='select-label'
			{...props}
		/>
	)
}

function SelectItem({
	className,
	children,
	...props
}: SelectPrimitive.Item.Props) {
	return (
		<SelectPrimitive.Item
			className={cn(
				'my-px flex cursor-pointer items-center overflow-x-hidden truncate rounded-md px-2 py-2.5 text-neutral-900 text-sm transition-all duration-150',
				'data-[disabled]:pointer-events-none data-[disabled]:text-neutral-400 data-[disabled]:opacity-50',
				'data-[highlighted]:bg-neutral-100',
				className
			)}
			data-slot='select-item'
			{...props}
		>
			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
			<SelectPrimitive.ItemIndicator
				render={
					<span className='pointer-events-none ml-auto flex size-4 shrink-0 items-center justify-center' />
				}
			>
				<CheckIcon size={16} />
			</SelectPrimitive.ItemIndicator>
		</SelectPrimitive.Item>
	)
}

function SelectSeparator({
	className,
	...props
}: SelectPrimitive.Separator.Props) {
	return (
		<SelectPrimitive.Separator
			className={cn(
				'pointer-events-none -mx-1 my-1 h-px bg-neutral-200',
				className
			)}
			data-slot='select-separator'
			{...props}
		/>
	)
}

function SelectScrollUpButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
	return (
		<SelectPrimitive.ScrollUpArrow
			className={cn(
				'top-0 z-10 flex w-full cursor-default items-center justify-center bg-neutral-white py-1',
				className
			)}
			data-slot='select-scroll-up-button'
			{...props}
		>
			<ChevronUpIcon size={16} />
		</SelectPrimitive.ScrollUpArrow>
	)
}

function SelectScrollDownButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
	return (
		<SelectPrimitive.ScrollDownArrow
			className={cn(
				'bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-neutral-white py-1',
				className
			)}
			data-slot='select-scroll-down-button'
			{...props}
		>
			<ChevronDownIcon size={16} />
		</SelectPrimitive.ScrollDownArrow>
	)
}

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue
}
